import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getRuntimeConfig } from '../../config/runtime.config';
import { ApiRoutes } from '../../config/constants';
import { StorageService } from '../storage.service';
import { LoginRequest, LoginResponse, RefreshResponse, ApiResponse, UserInfo } from './types';

class ApiService {
  private axiosInstance: AxiosInstance | null = null;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
  }> = [];

  private getAxios(): AxiosInstance {
    if (!this.axiosInstance) {
      const config = getRuntimeConfig();
      this.axiosInstance = axios.create({
        baseURL: config.api_url,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      this.setupInterceptors();
    }
    return this.axiosInstance;
  }

  private setupInterceptors(): void {
    if (!this.axiosInstance) return;

    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = StorageService.getToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                }
                return this.getAxios()(originalRequest);
              })
              .catch((err) => Promise.reject(err));
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const refreshToken = StorageService.getRefreshToken();
            if (!refreshToken) {
              throw new Error('No refresh token');
            }

            const response = await this.getAxios().post<RefreshResponse>(
              ApiRoutes.Auth.Refresh,
              { refreshToken }
            );

            const { accessToken } = response.data.data;
            StorageService.setToken(accessToken);

            this.processQueue(null, accessToken);

            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            }
            return this.getAxios()(originalRequest);
          } catch (refreshError) {
            this.processQueue(refreshError as Error, null);
            StorageService.clearAuth();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private processQueue(error: Error | null, token: string | null): void {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else if (token) {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.getAxios().post<LoginResponse>(
      ApiRoutes.Auth.Login,
      credentials
    );
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.getAxios().post(ApiRoutes.Auth.Logout);
    } finally {
      StorageService.clearAuth();
    }
  }

  async getMe(): Promise<ApiResponse<UserInfo>> {
    const response = await this.getAxios().get<ApiResponse<UserInfo>>(ApiRoutes.Auth.Me);
    return response.data;
  }

  async verifyToken(): Promise<boolean> {
    try {
      const response = await this.getAxios().get<ApiResponse<{ valid: boolean }>>(
        ApiRoutes.Auth.Verify
      );
      return response.data.success && response.data.data.valid;
    } catch {
      return false;
    }
  }

  async get<T>(url: string, params?: Record<string, unknown>): Promise<ApiResponse<T>> {
    const response = await this.getAxios().get<ApiResponse<T>>(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    const response = await this.getAxios().post<ApiResponse<T>>(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    const response = await this.getAxios().put<ApiResponse<T>>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    const response = await this.getAxios().delete<ApiResponse<T>>(url);
    return response.data;
  }
}

export const apiService = new ApiService();
